import { Check, Package, MapPin, Zap, Plus, Camera, RefreshCw, Search, X } from "lucide-react";

const COLORS = [
  { name: "Primary", var: "--color-primary", hex: "#4F4065", cls: "bg-primary", desc: "Brand color, buttons, active states, links" },
  { name: "Primary Light", var: "--color-primary-light", hex: "#D1C4E9", cls: "bg-primary-light", desc: "Light purple backgrounds, badges, secondary CTA" },
  { name: "Secondary", var: "--color-secondary", hex: "#A8BCA1", cls: "bg-secondary", desc: "Success states, nature accents, green highlights" },
  { name: "Secondary Light", var: "--color-secondary-light", hex: "#c8d8c3", cls: "bg-secondary-light", desc: "Light green tints" },
  { name: "Accent", var: "--color-accent", hex: "#8B6AAE", cls: "bg-accent", desc: "Warm purple accent, secondary actions (Quick Reset, Visual Scan)" },
  { name: "Neutral", var: "--color-neutral", hex: "#F7F9F7", cls: "bg-neutral", desc: "Page backgrounds" },
  { name: "Neutral Border", var: "--color-neutral-border", hex: "#E2E8E0", cls: "bg-neutral-border", desc: "Borders, dividers, input outlines" },
  { name: "Muted", var: "--color-muted", hex: "#DDE0DD", cls: "bg-muted", desc: "Inactive pills, toggle chips — contrasts against neutral bg and Ghost" },
];

const OPACITY_EXAMPLES = [
  { label: "primary/5", cls: "bg-primary/5" },
  { label: "primary/10", cls: "bg-primary/10" },
  { label: "primary/20", cls: "bg-primary/20" },
  { label: "accent/15", cls: "bg-accent/15" },
  { label: "secondary/10", cls: "bg-secondary/10" },
];

const RADII = [
  { label: "rounded-lg (8)", cls: "rounded-lg" },
  { label: "rounded-xl (12)", cls: "rounded-xl" },
  { label: "rounded-2xl (16)", cls: "rounded-2xl" },
  { label: "rounded-full", cls: "rounded-full" },
];

export default function DesignSystemPage() {
  return (
    <div className="min-h-screen bg-neutral">
      <div className="max-w-5xl mx-auto p-8 space-y-16">
        {/* Header */}
        <header className="space-y-2">
          <h1 className="text-4xl font-extrabold font-headline text-primary">Homethyst Design System</h1>
          <p className="text-gray-500">Reference guide for colors, typography, components, and patterns.</p>
        </header>

        {/* Colors */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-headline text-gray-900">Colors</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {COLORS.map((c) => (
              <div key={c.name} className="bg-white rounded-2xl border border-neutral-border/30 overflow-hidden">
                <div className={`${c.cls} h-20`} />
                <div className="p-4 space-y-1">
                  <div className="flex justify-between items-baseline">
                    <span className="font-semibold text-sm font-headline">{c.name}</span>
                    <code className="text-xs text-gray-400">{c.hex}</code>
                  </div>
                  <p className="text-xs text-gray-500">{c.desc}</p>
                  <code className="text-[10px] text-gray-400 block">{c.var}</code>
                </div>
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Opacity Variants</h3>
            <div className="flex flex-wrap gap-3">
              {OPACITY_EXAMPLES.map((o) => (
                <div key={o.label} className="text-center">
                  <div className={`${o.cls} w-16 h-16 rounded-xl border border-neutral-border/20`} />
                  <code className="text-[10px] text-gray-400 mt-1 block">{o.label}</code>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Typography */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-headline text-gray-900">Typography</h2>
          <div className="bg-white rounded-2xl border border-neutral-border/30 p-6 space-y-6">
            <div>
              <code className="text-[10px] text-gray-400 block mb-1">font-headline — Manrope</code>
              <p className="text-4xl font-extrabold font-headline text-gray-900">The quick brown fox</p>
            </div>
            <div>
              <code className="text-[10px] text-gray-400 block mb-1">font-body — Plus Jakarta Sans (default)</code>
              <p className="text-lg text-gray-700">The quick brown fox jumps over the lazy dog. This is the default body font used throughout the application for paragraphs, labels, and descriptions.</p>
            </div>
            <hr className="border-neutral-border/30" />
            <div className="space-y-3">
              <div><span className="text-3xl font-bold font-headline">Page Title</span> <code className="text-[10px] text-gray-400 ml-2">text-3xl font-bold font-headline</code></div>
              <div><span className="text-2xl font-bold font-headline">Section Heading</span> <code className="text-[10px] text-gray-400 ml-2">text-2xl font-bold font-headline</code></div>
              <div><span className="text-lg font-bold">Card Title</span> <code className="text-[10px] text-gray-400 ml-2">text-lg font-bold</code></div>
              <div><span className="text-sm font-medium">Label</span> <code className="text-[10px] text-gray-400 ml-2">text-sm font-medium</code></div>
              <div><span className="text-sm text-gray-500">Body text</span> <code className="text-[10px] text-gray-400 ml-2">text-sm text-gray-500</code></div>
              <div><span className="text-xs text-gray-400">Caption</span> <code className="text-[10px] text-gray-400 ml-2">text-xs text-gray-400</code></div>
            </div>
          </div>
        </section>

        {/* Border Radii */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-headline text-gray-900">Border Radii</h2>
          <div className="flex flex-wrap gap-6">
            {RADII.map((r) => (
              <div key={r.label} className="text-center">
                <div className={`${r.cls} w-20 h-20 bg-primary/10 border-2 border-primary/30`} />
                <code className="text-[10px] text-gray-400 mt-2 block">{r.label}</code>
              </div>
            ))}
          </div>
        </section>

        {/* Buttons */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-headline text-gray-900">Buttons</h2>
          <div className="bg-white rounded-2xl border border-neutral-border/30 p-6 space-y-6">
            <div className="flex flex-wrap gap-3 items-center">
              <button className="px-4 py-2 rounded-xl bg-primary text-white text-sm font-medium">Primary</button>
              <button className="px-4 py-2 rounded-xl bg-accent text-white text-sm font-medium">Accent</button>
              <button className="px-4 py-2 rounded-xl bg-primary-light text-primary text-sm font-medium">Secondary</button>
              <button className="px-4 py-2 rounded-xl bg-red-50 text-red-600 text-sm font-medium">Destructive</button>
              <button className="px-4 py-2 rounded-xl bg-primary/10 text-primary text-sm font-medium">Ghost</button>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <button className="px-6 py-3 rounded-xl bg-primary text-white text-sm font-medium flex items-center gap-1"><Plus className="w-4 h-4" />With Icon</button>
              <button className="px-3 py-1 rounded-full text-xs font-medium bg-primary text-white">Pill Active</button>
              <button className="px-3 py-1 rounded-full text-xs font-medium bg-muted text-gray-600">Pill Inactive</button>
            </div>
          </div>
        </section>

        {/* Form Elements */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-headline text-gray-900">Form Elements</h2>
          <div className="bg-white rounded-2xl border border-neutral-border/30 p-6 space-y-4 max-w-md">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Text Input</label>
              <input placeholder="Placeholder text..." className="w-full px-4 py-2.5 rounded-xl border border-neutral-border/40 text-sm focus:outline-none focus:border-primary" readOnly />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Search Input</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input placeholder="Search items..." className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-neutral-border/40 text-sm focus:outline-none focus:border-primary" readOnly />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Tags</label>
              <div className="flex flex-wrap gap-1.5 p-2 rounded-xl border border-neutral-border/40 min-h-[42px]">
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">kitchen <X className="w-3 h-3" /></span>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs">fragile <X className="w-3 h-3" /></span>
              </div>
            </div>
          </div>
        </section>

        {/* Cards & Patterns */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-headline text-gray-900">Cards &amp; Patterns</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Stat card */}
            <div className="p-4 rounded-2xl bg-white border border-neutral-border/20 text-center">
              <div className="text-2xl font-bold text-primary">42</div>
              <div className="text-xs text-gray-400">Items</div>
            </div>

            {/* Item row */}
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-neutral-border/20 hover:border-primary/30 transition-all cursor-pointer">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center"><Package className="w-5 h-5 text-gray-300" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between"><h3 className="font-medium text-sm truncate">Example Item</h3><span className="text-xs text-gray-400">x3</span></div>
                <div className="flex gap-1 mt-1">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />Kitchen</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">fragile</span>
                </div>
              </div>
            </div>

            {/* Quick action card */}
            <div className="flex flex-col items-start gap-2 p-5 rounded-2xl border border-neutral-border/30 bg-white text-left">
              <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Plus className="w-6 h-6" /></div>
              <div><div className="font-semibold text-sm text-gray-900">Add Item</div><div className="text-xs text-gray-400">Log a new item</div></div>
            </div>

            {/* Accent action card */}
            <div className="flex flex-col items-start gap-2 p-5 rounded-2xl border border-neutral-border/30 bg-white text-left">
              <div className="w-11 h-11 rounded-xl bg-accent/15 text-accent flex items-center justify-center"><RefreshCw className="w-6 h-6" /></div>
              <div><div className="font-semibold text-sm text-gray-900">Quick Reset</div><div className="text-xs text-gray-400">Declutter a space</div></div>
            </div>
          </div>

          {/* Onboarding / progress pattern */}
          <div className="bg-white rounded-2xl border border-primary/20 p-6 space-y-4 max-w-md">
            <div className="flex justify-between items-start">
              <div><h2 className="text-lg font-bold">Getting Started</h2><p className="text-sm text-gray-400">Build your digital home</p></div>
              <span className="text-sm font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">67%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full"><div className="h-full bg-primary rounded-full" style={{ width: "67%" }} /></div>
            <div className="space-y-3">
              {[{ label: "Map out your spaces", done: true }, { label: "Catalog your first item", done: true }, { label: "Reach 5 items", done: false }].map((task, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${task.done ? "bg-primary border-primary" : "border-gray-200"}`}>
                    {task.done && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className={`text-sm ${task.done ? "text-gray-400 line-through" : "font-medium"}`}>{task.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Spacing Reference */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-headline text-gray-900">Spacing Tokens</h2>
          <div className="bg-white rounded-2xl border border-neutral-border/30 p-6">
            <div className="space-y-3">
              {[{ name: "xs", val: "4px" }, { name: "sm", val: "8px" }, { name: "md", val: "16px" }, { name: "lg", val: "20px" }, { name: "xl", val: "24px" }, { name: "2xl", val: "32px" }].map((s) => (
                <div key={s.name} className="flex items-center gap-4">
                  <code className="text-xs text-gray-500 w-12">{s.name}</code>
                  <div className="bg-primary/20 rounded" style={{ width: s.val, height: 16 }} />
                  <span className="text-xs text-gray-400">{s.val}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Theme Reference */}
        <section className="space-y-6">
          <h2 className="text-2xl font-bold font-headline text-gray-900">Mobile Theme Tokens</h2>
          <div className="bg-white rounded-2xl border border-neutral-border/30 p-6 space-y-4">
            <p className="text-sm text-gray-500">Mobile uses the same shared tokens from <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">@homethyst/shared/theme</code>, accessed via <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">packages/mobile/src/theme.ts</code>.</p>
            <div className="text-sm space-y-1 font-mono text-gray-600">
              <p>colors.primary → <span className="text-primary font-semibold">#4F4065</span></p>
              <p>colors.accent → <span className="text-accent font-semibold">#8B6AAE</span></p>
              <p>colors.secondary → <span className="text-secondary font-semibold">#A8BCA1</span></p>
              <p>colors.neutral → #F7F9F7</p>
              <p>colors.neutralBorder → #E2E8E0</p>
              <p>colors.muted → #DDE0DD</p>
              <p>radii.sm=8 · radii.md=12 · radii.lg=16 · radii.xl=20</p>
              <p>withOpacity(colors.primary, 0.1) → rgba(79, 64, 101, 0.1)</p>
            </div>
            <p className="text-xs text-gray-400">Mobile uses system fonts. Typography tokens are platform-aware.</p>
          </div>
        </section>

        <footer className="text-xs text-gray-400 pt-8 border-t border-neutral-border/30">
          Homethyst Design System · Updated April 2026
        </footer>
      </div>
    </div>
  );
}
